import { Component, DestroyRef, OnInit, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule, RouterOutlet } from '@angular/router';
import { catchError, finalize, map, of } from 'rxjs';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { UISkeletonTableComponent } from 'src/app/shared/ui/skeletons/table/skeleton-table.component';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { ReactiveFormsModule } from '@angular/forms';
import { AssetFlatListTableComponent } from '../../shared/common-ui-components/asset-table/asset-flatlist-table.component';
import { UISkeletonAssetsComponent } from 'src/app/shared/ui/skeletons/assets/skeleton-assets.component';
import { PostHogService } from 'src/app/shared/services/post-hog.service';
import { CreateCanvasComponent } from './components/create-canvas/create-canvas.component';

@Component({
  selector: 'app-canvas',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterModule,
    AssetFlatListTableComponent,
    ReactiveFormsModule,
    UISkeletonTableComponent,
    UISkeletonAssetsComponent,
    UIIconsComponent,
    CreateCanvasComponent
  ],
  templateUrl: './canvas.component.html',
  styleUrl: './canvas.component.css'
})
export class CanvasComponent implements OnInit {
  private assetsApiService = inject(AssetsApiService);
  private route = inject(ActivatedRoute);
  private postHogService = inject(PostHogService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  public assetType = 'CANVAS';
  public showCreateCanvasModal = signal<boolean>(false);
  public showManageCanvasModal = signal<boolean>(false);
  public loadingCanvases = signal<boolean>(false);
  public canvases = signal<any[]>([]);
  public selectedCanvas = signal<any>(null);

  ngOnInit(): void {
    const currentUrl = this.router.url;
    const isCanvasDetailView = currentUrl.includes('/dashboard/canvas/') &&
                              !currentUrl.endsWith('/dashboard/canvas') &&
                              !currentUrl.endsWith('/dashboard/canvas/create');

    if (isCanvasDetailView) {
      this.showCreateCanvasModal.set(true);
    }

    this.getCanvases();
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params: any) => {
          if (params.add) {
            this.openCreateCanvasModal();
          }
        },
      });

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params['id']) {
          this.showCreateCanvasModal.set(true);
        }
      });
  }

  public setSelectedCanvas(canvas: any): void {
    this.selectedCanvas.set(canvas);
  }

  public setShowCreateCanvasModal(value: boolean): void {
    this.showCreateCanvasModal.set(true);
    this.router.navigate(['/dashboard/canvas/create']);
  }

  public openCreateCanvasModal(canvas?: any): void {
    this.postHogService.capture('ADD CANVAS');
    this.selectedCanvas.set(canvas ?? null);
    this.showCreateCanvasModal.set(true);
    this.router.navigate(['/dashboard/canvas/create']);
  }

  public getCanvases(): void {
    this.loadingCanvases.set(true);
    this.assetsApiService
      .getAssetByType(this.assetType, 'network-only')
      .pipe(
        map((response) => response?.data?.assetsByType || []),
        catchError((error) => {
          console.error('Error getting asset by type: CANVAS:', error);
          this.loadingCanvases.set(false);
          return of([]);
        }),
        finalize(() => this.loadingCanvases.set(false)),
      )
      .subscribe({
        next: (canvases) => this.canvases.set(canvases),
      });
  }

  public onCreateCanvasSuccess(): void {
    this.setShowCreateCanvasModal(false);
    this.getCanvases();
  }

  public onCloseCreateCanvas(): void {
    this.showCreateCanvasModal.set(false);
    this.router.navigate(['/dashboard/canvas']);
  }

  public onSuccessManageCanvasModal(): void {
    this.showManageCanvasModal.set(false);
    this.getCanvases();
  }

  public onCloseManageCanvasModal(): void {
    this.showManageCanvasModal.set(false);
  }

  public setShowManageCanvasModal(value: boolean): void {
    this.showManageCanvasModal.set(value);
  }

  public handleEditAction(asset: any): void {
    this.selectedCanvas.set(asset);
    this.showCreateCanvasModal.set(true);
    this.router.navigate(['/dashboard/canvas', asset.id]);
  }

  handleDeleteAction(asset: any): void {
    this.postHogService.capture('DELETE CANVAS', { canvasId: asset.id });
    this.assetsApiService.deleteAsset(asset.id).subscribe({
      next: () => {
        this.getCanvases();
      },
      error: (error) => {
        console.error('Error deleting canvas:', error);
      }
    });
  }
}
